// The workspace you are in, and the way to the others.
//
// The name in the top left is the obvious place to ask "which one is this, and
// what else is there", so the switcher lives on it rather than buried in the
// account menu.
//
// The chevron is always there, even with a single workspace, because this menu is
// also the only clean way to REACH another one: somebody invited to a second team
// has an address in an email and nowhere in the app to put it. "Sign in to another
// workspace" takes the slug and goes to that door, where a different email can be
// used — Better Auth keeps both identities on the device and the account menu
// switches between them.
//
// Being listed here grants nothing. The list is the membership projection in D1,
// a cache of every workspace's roster; arriving at one you have since been removed
// from gets the sign-in screen like anyone else.

import { useEffect, useRef, useState } from 'react'

import { blobUrl, workspaceSlug } from '../lib/api'
import { displayName } from '../lib/brand'
import { goAs, reachableWorkspaces, superadminHop } from '../lib/identities'
import { useStore } from '../lib/store'
import { useMe } from '../lib/use-me'
import { Icon } from './Icon'
import { Brand } from './Mark'

export function WorkspaceSwitcher() {
  const { brand } = useStore()
  const me = useMe()
  const [open, setOpen] = useState(false)
  const [adding, setAdding] = useState(false)
  const [slug, setSlug] = useState('')
  const [busy, setBusy] = useState(false)
  const ref = useRef<HTMLDivElement | null>(null)
  const here = workspaceSlug()

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false)
        setAdding(false)
      }
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  // Every workspace this DEVICE can open, not just the active identity's — and for
  // each, the identity it opens as. See lib/identities.ts.
  const reachable = reachableWorkspaces(me)

  /** Somebody on this device is a superadmin, even if it is not who is active. */
  const adminIdentity = superadminHop(me)

  async function goTo(to: { slug?: string; token: string | null; path?: string }) {
    setBusy(true)
    await goAs(to.token, to.path ?? `/w/${encodeURIComponent(to.slug ?? '')}`)
  }

  const mark = brand.logoKey ? (
    <img
      src={blobUrl(brand.logoKey)}
      alt=""
      className="h-6 w-6 shrink-0 rounded-[30%] object-contain"
    />
  ) : (
    <Brand size={24} />
  )
  const label = (
    <span className="text-lead hidden max-w-[16rem] truncate font-semibold tracking-tight sm:inline">
      {displayName(brand)}
    </span>
  )

  return (
    <div ref={ref} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="rounded-control flex items-center gap-2 py-0.5 pr-1 hover:bg-[var(--rail-hover)]"
        aria-haspopup="menu"
        aria-expanded={open}
        title="Switch workspace"
      >
        {mark}
        {label}
        <Icon name="ChevronDown" size={14} style={{ color: 'var(--muted)' }} />
      </button>

      {open ? (
        <div
          role="menu"
          className="rounded-control absolute left-0 z-40 mt-1 w-[16rem] border py-1 shadow-lg"
          style={{ background: 'var(--panel)', borderColor: 'var(--border)' }}
        >
          {reachable.length > 0 ? (
            <>
              <p className="eyebrow px-3 pt-1 pb-0.5">Workspaces</p>
              {reachable.map((s) => {
                const current = s.slug === here
                return (
                  <button
                    key={s.slug}
                    type="button"
                    role="menuitem"
                    disabled={busy}
                    onClick={() => void goTo(s)}
                    className="text-body flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-[var(--rail-hover)] disabled:opacity-50"
                    style={{ fontWeight: current ? 560 : 450 }}
                  >
                    <span className="w-3.5 shrink-0">
                      {current ? <Icon name="Check" size={14} /> : null}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate">{s.name}</span>
                      {/* Named, because opening it changes who you are signed in as and
                          that should not be a surprise. */}
                      {s.via ? (
                        <span
                          className="meta block truncate"
                          style={{ fontSize: 'var(--text-label)' }}
                        >
                          as {s.via}
                        </span>
                      ) : null}
                    </span>
                    <span
                      className="num shrink-0"
                      style={{ fontSize: 'var(--text-label)', color: 'var(--faint)' }}
                    >
                      /{s.slug}
                    </span>
                  </button>
                )
              })}
            </>
          ) : null}
          <div className="my-1 h-px" style={{ background: 'var(--border)' }} />
          {adding ? (
            <form
              className="px-3 py-2"
              onSubmit={(e) => {
                e.preventDefault()
                const s = slug.trim().toLowerCase()
                if (s) location.assign(`/w/${encodeURIComponent(s)}`)
              }}
            >
              <label className="eyebrow mb-1 block">Workspace address</label>
              <div className="flex items-center gap-1.5">
                <span
                  className="num shrink-0"
                  style={{ fontSize: 'var(--text-label)', color: 'var(--faint)' }}
                >
                  /w/
                </span>
                <input
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  placeholder="theirteam"
                  autoFocus
                  className="num rounded-control text-meta min-w-0 flex-1 border px-2 py-1 outline-none"
                  style={{ borderColor: 'var(--border)', background: 'var(--bg)' }}
                />
              </div>
              <p className="note mt-1.5 leading-relaxed">
                The team that invited you gave you this address. You can sign in there with a
                different email; both stay signed in on this device.
              </p>
            </form>
          ) : (
            <button
              type="button"
              role="menuitem"
              onClick={() => setAdding(true)}
              className="text-body flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-[var(--rail-hover)]"
            >
              <span className="w-3.5 shrink-0">
                <Icon name="Plus" size={14} />
              </span>
              Sign in to another workspace
            </button>
          )}
          {adminIdentity ? (
            <button
              type="button"
              role="menuitem"
              disabled={busy}
              onClick={() => void goTo({ token: adminIdentity.token, path: '/admin' })}
              className="text-body block w-full px-3 py-1.5 text-left hover:bg-[var(--rail-hover)] disabled:opacity-50"
              style={{ paddingLeft: '2.4rem' }}
            >
              Manage workspaces
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
